import React, { useState, useCallback, MouseEvent } from 'react';
import { Editor } from '@tiptap/react';
import { Link as LinkIcon, LinkOff } from '@mui/icons-material';
import { Popover, TextField, Button } from '@mui/material';

import classes from './LinkMenuButton.module.scss';
import { MenuButton } from '../MenuButton/MenuButton';

type LinkMenuButtonProps = {
  editor: Editor;
};

export const LinkMenuButton = ({ editor }: LinkMenuButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [url, setUrl] = useState('');

  const isPopoverOpen = !!anchorEl;
  const isLinkActive = editor.isActive('link');

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const previousUrl = editor.getAttributes('link').href || '';
      setUrl(previousUrl);
      setAnchorEl(event.currentTarget);
    },
    [editor],
  );

  const handleClose = () => {
    setAnchorEl(null);
    setUrl('');
  };

  const handleSubmit = () => {
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    handleClose();
  };

  const handleRemove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    handleClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      <MenuButton
        onClick={handleClick}
        isActive={isLinkActive}
        tooltip="Link"
        icon={<LinkIcon fontSize="small" />}
      />

      <Popover open={isPopoverOpen} anchorEl={anchorEl} onClose={handleClose}>
        <div className={classes.popover}>
          <TextField
            size="small"
            placeholder="Enter URL"
            value={url}
            fullWidth
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <div className={classes.actions}>
            <Button size="small" variant="contained" onClick={handleSubmit}>
              Apply
            </Button>
            {isLinkActive && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleRemove}
                startIcon={<LinkOff fontSize="small" />}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};
